import type { Date as PDate } from "../../generated/core/v1/date";
import { type Event, Event_Type } from "../../generated/core/v1/events";
import {
  type Balance,
  EntityKind,
  type FinancialPlanData,
  type State,
} from "../../generated/core/v1/financial_plan";
import { addMonths, compareDates, dateKey, isSet, yearFraction } from "../lib/dates";

/**
 * plan-view: pure selectors over the engine's facts-only plan (architecture-redesign.md item I).
 * They select, filter, sum and group. They never apply a financial rule (no rates, limits or tax):
 * every quantity that needs one comes from the engine.
 *
 * Plans come in pairs: Abi's plan and the current trajectory ("naive"). Either may be missing.
 */
export type Plan = FinancialPlanData | undefined | null;

export type Point = { date: PDate; x: number; value: number };

export const DEBT_KINDS = new Set([EntityKind.MORTGAGE, EntityKind.LOAN, EntityKind.CREDIT_CARD]);
export const CASH_KINDS = new Set([EntityKind.CHEQUING, EntityKind.SAVINGS]);
export const INVESTMENT_KINDS = new Set([
  EntityKind.RRSP,
  EntityKind.TFSA,
  EntityKind.FHSA,
  EntityKind.RESP,
  EntityKind.RRIF,
  EntityKind.NON_REGISTERED,
  EntityKind.DC_PENSION,
]);

const sum = (xs: Balance[]) => xs.reduce((s, b) => s + b.balance, 0);

export function startDate(plan: Plan): PDate | undefined {
  return plan?.meta?.startDate ?? plan?.monthlyStates[0]?.date;
}

export function endDate(plan: Plan): PDate | undefined {
  return plan?.meta?.endDate ?? plan?.monthlyStates.at(-1)?.date;
}

/** The first state of the plan (its "today"). */
export function firstState(plan: Plan): State | undefined {
  return plan?.monthlyStates[0] ?? plan?.yearOneDailyStates[0];
}

/** The latest monthly state on or before `date` (the first state when `date` is earlier). */
export function stateAt(plan: Plan, date: PDate | undefined): State | undefined {
  if (!plan || !date) return undefined;
  const states = plan.monthlyStates;
  let found: State | undefined;
  for (const s of states) {
    if (compareDates(s.date, date) <= 0) found = s;
    else break;
  }
  return found ?? states[0];
}

/** Everything held: accounts and assets (property). */
export function ownTotal(state: State | undefined): number {
  if (!state) return 0;
  return sum(state.accounts) + sum(state.assets);
}

/** Everything owed (debts are positive amounts). */
export function oweTotal(state: State | undefined): number {
  return state ? sum(state.debts) : 0;
}

export function netWorth(state: State | undefined): number {
  return ownTotal(state) - oweTotal(state);
}

/** All balances of a state, with which side of the ledger they're on. */
export function allBalances(state: State | undefined): Balance[] {
  if (!state) return [];
  return [...state.accounts, ...state.assets, ...state.debts];
}

/** Sum of balances of the given kinds (debts count positive). */
export function sumKinds(state: State | undefined, kinds: Set<EntityKind> | EntityKind[]): number {
  const set = kinds instanceof Set ? kinds : new Set(kinds);
  return allBalances(state)
    .filter((b) => set.has(b.kind))
    .reduce((s, b) => s + b.balance, 0);
}

/**
 * One state per calendar year: the plan's first state, then the last state of each year. Charts
 * that run decades use this.
 */
export function yearlyStates(plan: Plan): State[] {
  if (!plan) return [];
  const out: State[] = [];
  const states = plan.monthlyStates;
  states.forEach((s, i) => {
    const next = states[i + 1];
    if (i === 0 || !next || next.date!.year !== s.date!.year) out.push(s);
  });
  // The first state and the end of its own year can coincide in year; keep both only if distinct.
  return out.filter((s, i) => i === 0 || dateKey(s.date) !== dateKey(out[i - 1].date));
}

/** Net worth through the plan, one point per year (plus the start). */
export function netWorthSeries(plan: Plan): Point[] {
  return yearlyStates(plan).map((s) => ({
    date: s.date!,
    x: yearFraction(s.date!),
    value: netWorth(s),
  }));
}

/** A series of a selected quantity, one point per year. */
export function yearlySeries(plan: Plan, pick: (s: State) => number): Point[] {
  return yearlyStates(plan).map((s) => ({
    date: s.date!,
    x: yearFraction(s.date!),
    value: pick(s),
  }));
}

/** Events of the given type(s), in date order. */
export function eventsOf(plan: Plan, ...types: Event_Type[]): Event[] {
  if (!plan) return [];
  const set = new Set(types);
  return plan.events.filter((e) => set.has(e.type)).sort((a, b) => compareDates(a.date, b.date));
}

/** Events within [from, to). */
export function eventsBetween(
  plan: Plan,
  from: PDate | undefined,
  to: PDate | undefined,
  ...types: Event_Type[]
): Event[] {
  return eventsOf(plan, ...types).filter(
    (e) => (!from || compareDates(e.date, from) >= 0) && (!to || compareDates(e.date, to) < 0),
  );
}

/** [start, start + months): the plan's first `months` months. */
export function planWindow(plan: Plan, months: number): { from?: PDate; to?: PDate } {
  const from = startDate(plan);
  return { from, to: from ? addMonths(from, months) : undefined };
}

/** Which kind each entity uuid has, from the plan's balances. */
export function entityKinds(plan: Plan): Map<string, EntityKind> {
  const map = new Map<string, EntityKind>();
  const s = firstState(plan);
  const visit = (state: State | undefined) =>
    allBalances(state).forEach((b) => map.has(b.entityUuid) || map.set(b.entityUuid, b.kind));
  visit(s);
  plan?.monthlyStates.forEach(visit);
  return map;
}

export function hasPlanData(plan: Plan): plan is FinancialPlanData {
  return !!plan && (plan.monthlyStates.length > 0 || plan.events.length > 0);
}

export { isSet };

/**
 * Today's net worth: what the plan's first state owns less what it owes. The one figure every page
 * quotes for it (v45: the headline, the charts' starting point, Home, and Achievements read this),
 * so none can disagree. The first state is the end of the plan's first day (`meta.start_date`;
 * the engine's states are end-of-day balances), so it already has any pay that came in that day and
 * the plan's first moves: right after onboarding it can differ from the balances entered (QA 7:
 * $49,040 against $43,000). Pages that show it say so (`FIRST_DAY_NOTE`).
 */
export function netWorthToday(plan: Plan): number {
  return netWorth(firstState(plan));
}

/**
 * What a "today" figure from the plan's first state is, for pages that show one: the end of the
 * plan's first day, after that day's pay and Abi's first moves.
 */
export const FIRST_DAY_NOTE =
  "Balances at the end of the plan’s first day, {date}: after any pay that came in that day and the first moves Abi’s plan makes, so they can differ from what you entered.";
